import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Button, Form, FormItem } from '../Ant';
import CustomDatePicker from '../Ant/CustomDatePicker/CustomDatePicker';
import { CalendarOutline } from '../icons';
import {
  PreliminaryInvoiceCreationRequest,
  PreliminaryInvoiceCreationType,
} from '../../interface/invoice';

const formatApiDate = (date: Dayjs) => date.format('YYYY-MM-DD');

interface PreliminaryInvoiceUntilDateFormProps {
  isEntryDateLocked: boolean;
  onConfirm: (request: PreliminaryInvoiceCreationRequest) => void;
}

const PreliminaryInvoiceUntilDateForm = ({
  isEntryDateLocked,
  onConfirm,
}: PreliminaryInvoiceUntilDateFormProps) => {
  const { t } = useTranslation();
  const [periodEndDate, setPeriodEndDate] = useState<Dayjs>(() => dayjs());
  const [entryDate, setEntryDate] = useState<Dayjs>(() => dayjs());

  const confirmUntilDate = () =>
    onConfirm({
      type: PreliminaryInvoiceCreationType.UNTIL_DATE,
      periodEndDate: formatApiDate(periodEndDate),
      entryDate: isEntryDateLocked ? undefined : formatApiDate(entryDate),
    });

  return (
    <Form
      layout="vertical"
      className="w-full d-flex fd-column gap-gutter"
      data-testid="preliminary-invoice-until-date-form"
    >
      <FormItem
        label={t('matter:periodEndDate')}
        htmlFor="preliminary-invoice-period-end-date"
        data-testid="preliminary-invoice-period-end-date-item"
      >
        <CustomDatePicker
          id="preliminary-invoice-period-end-date"
          data-testid="preliminary-invoice-period-end-date"
          className="w-full"
          placeholder={t('matter:selectPeriodEndDate')}
          suffixIcon={<CalendarOutline className="calendar-icon" />}
          placement="bottomLeft"
          value={periodEndDate}
          allowClear={false}
          showWeek
          superNextIcon={null}
          superPrevIcon={null}
          onChange={(date) => {
            if (date) setPeriodEndDate(date);
          }}
        />
      </FormItem>
      <FormItem
        label={t('preliminaryInvoice:entryDate')}
        htmlFor="preliminary-invoice-entry-date"
        data-testid="preliminary-invoice-entry-date-item"
      >
        <CustomDatePicker
          id="preliminary-invoice-entry-date"
          data-testid="preliminary-invoice-entry-date"
          className="w-full"
          placeholder={t('matter:selectEntryDate')}
          suffixIcon={<CalendarOutline className="calendar-icon" />}
          placement="bottomLeft"
          disabled={isEntryDateLocked}
          value={entryDate}
          allowClear={false}
          showWeek
          superNextIcon={null}
          superPrevIcon={null}
          onChange={(date) => {
            if (date) setEntryDate(date);
          }}
        />
      </FormItem>
      <Button
        data-testid="create-preliminary-invoice-confirm"
        type="primary"
        size="large"
        block
        onClick={confirmUntilDate}
      >
        {t('matter:createPreliminaryInvoice')}
      </Button>
    </Form>
  );
};

export default PreliminaryInvoiceUntilDateForm;
